/**
 * useFloatingWindow — Ziehen/Skalieren/Rand-Andocken für den KI-Assistenten (Redesign 28.08.2026,
 * Antoine: "schwebendes Fenster, das ich überall hinziehen kann und in der Größe/Form jederzeit
 * verändern kann", nach einem freigegebenen Prototyp im Apple-Glas-Stil).
 *
 * App-weite Erweiterung 28.08.2026 (Antoine, wörtlich: "Da[s] betrifft das gesamte Tool und alle
 * Funktionen"):
 *   - Skalieren an allen VIER Ecken statt nur unten rechts — `onResizeHandlePointerDown` nimmt
 *     jetzt eine `corner` an, exakt die Diagonal-Logik aus dem Prototyp
 *     (schwebender-assistent.html Zeilen 680-696): welche Kante fest bleibt (x/y wandert mit)
 *     hängt von der gewählten Ecke ab, nicht mehr fix von unten-rechts.
 *   - `collapsed` — eingeklappt auf eine feste Leiste am rechten Rand (Prototyp: `.edge-tab`,
 *     Zeilen 328-344/507-515) statt komplettem Verschwinden. Ein eigenes Bit NEBEN `mode`
 *     (nicht selbst ein `DockMode`) — CopilotPanel rendert bei `collapsed` statt des vollen
 *     Fensters nur noch den kleinen Tab; ein Klick darauf ruft `expand()`.
 *
 * Nur ab dem lg-Breakpoint aktiv (Tailwind `min-width:1024px`, siehe LG_QUERY) — auf Mobil bleibt
 * der Assistent die bestehende Vollbild-Schublade (CopilotPanel entscheidet selbst per `active`,
 * ob es die Geometrie dieses Hooks anwendet oder die alten Mobil-Klassen). `collapsed` ist damit
 * implizit auch nur ab lg wirksam — CopilotPanel rendert die Randleiste nur, wenn `active`
 * zusätzlich zutrifft.
 *
 * Bewusst weiterhin NICHT Teil dieses Hooks: das globale Ein-/Ausblenden der GANZEN Spalte
 * (Topbar-Knopf `toggleCopilot`, `CopilotProvider.isOpen`) — das bleibt der App-weite An/Aus-
 * Schalter. `collapsed` ist ein Zustand INNERHALB von "isOpen" (Randleiste statt volles Fenster,
 * nicht "ganz weg") — der Header-Knopf "einklappen" ruft ab jetzt `collapse()` statt `close()`.
 */
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

const STORAGE_KEY = "maestro.copilot.floatwindow.v1";
const LG_QUERY = "(min-width: 1024px)";

const MIN_W = 320;
const MIN_H = 360;
const MAX_W = 560;
const EDGE_MARGIN = 16;
const SNAP_ZONE = 44;
/** Breite der festen Spalte im "pinned"-Modus — wie im Prototyp (`--pinned-w:380px`) konstant,
 *  nicht ziehbar/skalierbar (deckungsgleich mit der bisherigen `COPILOT_DEFAULT`-Breite in
 *  AppShell.tsx). CopilotPanel.tsx/AppShell.tsx tragen "380px" zusätzlich als STATISCHES
 *  Tailwind-Literal (der Build-Scanner braucht den Klassennamen wörtlich im Quelltext) — beide
 *  Stellen müssen mit diesem Wert synchron bleiben. */
export const PINNED_W = 380;

export type DockMode = "float" | "dock-left" | "dock-right" | "pinned";
/** Welche Ecke ein Skalier-Griff bedient — wie `data-corner` im Prototyp. */
export type ResizeCorner = "nw" | "ne" | "sw" | "se";

interface Geometry {
  x: number;
  y: number;
  w: number;
  h: number;
  mode: DockMode;
}

const DEFAULT_W = 384;
const DEFAULT_H = 560;

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * Klammert x/y/w/h auf gueltige Werte fuer den "float"-Modus (wie der Resize-Effekt unten und der
 * Nicht-Andock-Zweig von `onHeaderPointerDown`). Gebraucht insbesondere beim Loesen aus "pinned"
 * (`togglePin`): waehrend Andocken/Verankern sind x/y NIE die tatsaechliche Bildschirmposition
 * (beide Modi positionieren ueber `right:0`/`left:0`, nicht ueber die gespeicherten Werte) — kommt
 * "pinned" direkt aus einem vorherigen Andock-Snap (statt aus echtem Ziehen, das x/y beim
 * Verlassen des Andockens live aus dem tatsaechlichen Rechteck neu liest, siehe
 * `onHeaderPointerDown`), steht in x/y sonst der rohe Loslass-Punkt des Andock-Zugs — oft weit
 * ausserhalb des Viewports, sobald daraus wieder ein schwebendes Fenster mit eigener Breite wird. */
function clampFloatGeometry(g: Geometry): Geometry {
  if (typeof window === "undefined") return g;
  const w = clamp(g.w, MIN_W, MAX_W);
  const h = clamp(g.h, MIN_H, window.innerHeight - EDGE_MARGIN * 2);
  const x = clamp(g.x, EDGE_MARGIN, Math.max(EDGE_MARGIN, window.innerWidth - w - EDGE_MARGIN));
  const y = clamp(g.y, EDGE_MARGIN, Math.max(EDGE_MARGIN, window.innerHeight - h - EDGE_MARGIN));
  return { ...g, w, h, x, y };
}

function readStored(): Geometry | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Geometry>;
    if (typeof parsed.w !== "number" || typeof parsed.h !== "number") return null;
    const mode: DockMode =
      parsed.mode === "dock-left" || parsed.mode === "dock-right" || parsed.mode === "pinned" ? parsed.mode : "float";
    return {
      x: typeof parsed.x === "number" ? parsed.x : 0,
      y: typeof parsed.y === "number" ? parsed.y : 0,
      w: clamp(parsed.w, MIN_W, MAX_W),
      h: clamp(parsed.h, MIN_H, 4000),
      mode,
    };
  } catch {
    return null;
  }
}

function writeStored(g: Geometry): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(g));
  } catch {
    /* localStorage nicht verfügbar (privater Modus o.ä.) — die Sitzung bleibt trotzdem nutzbar. */
  }
}

/** Eingeklappt auf die Randleiste — bewusst ein EIGENER Storage-Key statt Felder in `Geometry`:
 *  die Position/Größe soll erhalten bleiben, unabhängig davon, ob gerade die Randleiste oder das
 *  volle Fenster sichtbar ist. `wasPinned` merkt sich beim Einklappen, ob zuvor verankert war —
 *  bestimmt, wohin die Randleiste beim Ausklappen zurückkehrt (Prototyp-Zeile 725:
 *  `wasPinned ? "pinned" : "dock-right"`). */
const COLLAPSE_KEY = "maestro.copilot.floatwindow.collapsed.v1";

interface CollapseState {
  collapsed: boolean;
  wasPinned: boolean;
}

function readCollapseState(): CollapseState {
  if (typeof window === "undefined") return { collapsed: false, wasPinned: false };
  try {
    const raw = window.localStorage.getItem(COLLAPSE_KEY);
    if (!raw) return { collapsed: false, wasPinned: false };
    const parsed = JSON.parse(raw) as Partial<CollapseState>;
    return { collapsed: parsed.collapsed === true, wasPinned: parsed.wasPinned === true };
  } catch {
    return { collapsed: false, wasPinned: false };
  }
}

function writeCollapseState(s: CollapseState): void {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, JSON.stringify(s));
  } catch {
    /* localStorage nicht verfügbar — die Sitzung bleibt trotzdem nutzbar. */
  }
}

function defaultGeometry(): Geometry {
  const w = DEFAULT_W;
  const h = typeof window === "undefined" ? DEFAULT_H : clamp(window.innerHeight - 120, MIN_H, 720);
  const x = typeof window === "undefined" ? EDGE_MARGIN : Math.max(EDGE_MARGIN, window.innerWidth - w - 32);
  const y = typeof window === "undefined" ? EDGE_MARGIN : Math.max(EDGE_MARGIN, window.innerHeight - h - 96);
  return { x, y, w, h, mode: "float" };
}

export interface FloatingWindowApi {
  /** true ab lg-Breakpoint — nur dann liefert `style` eine Geometrie und die Handles greifen. */
  active: boolean;
  mode: DockMode;
  /** Eingeklappt auf die Randleiste (`.edge-tab`-Äquivalent) — CopilotPanel rendert dann statt des
   *  vollen Fensters nur noch den kleinen Tab. Unabhängig vom globalen `CopilotProvider.isOpen`. */
  collapsed: boolean;
  dragging: boolean;
  resizing: boolean;
  /** Inline-Style für den Fenster-Container; `undefined` unterhalb von lg (Mobil-Klassen greifen). */
  style: React.CSSProperties | undefined;
  /** Am Header-Ziehgriff einhängen. Ignoriert Klicks auf Buttons/Links darin (kein Drag-Start) und
   *  im "pinned"-Modus grundsätzlich (Prototyp: "fest verankert wird nur ueber den Anheften-Knopf
   *  geloest", Zeile 620) — kein versehentliches Verschieben aus der festen Spalte heraus. */
  onHeaderPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  /** An einem der vier Ecken-Ziehgriffe einhängen (nur wirksam im "float"-Modus). */
  onResizeHandlePointerDown: (e: ReactPointerEvent<HTMLElement>, corner: ResizeCorner) => void;
  /** Auf die Randleiste einklappen. */
  collapse: () => void;
  /** Randleiste wieder aufklappen — Modus wird wie im Prototyp NICHT auf den Stand vor dem
   *  Einklappen zurückgesetzt, sondern auf "pinned" (falls zuvor verankert) oder sonst "dock-right". */
  expand: () => void;
  /** Fest verankern umschalten (Prototyp: #pinBtn, Zeile 740-757). */
  togglePin: () => void;
}

export function useFloatingWindow(): FloatingWindowApi {
  const [active, setActive] = useState<boolean>(() => (typeof window === "undefined" ? false : window.matchMedia(LG_QUERY).matches));
  const [geo, setGeo] = useState<Geometry>(() => readStored() ?? defaultGeometry());
  const [collapseState, setCollapseState] = useState<CollapseState>(readCollapseState);
  const collapsed = collapseState.collapsed;
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  // Beim ersten Rendern (ohne gespeicherte Position) sinnvoll unten rechts platzieren.
  const initedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(LG_QUERY);
    const onChange = () => setActive(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (initedRef.current) return;
    initedRef.current = true;
    if (!readStored()) setGeo(defaultGeometry());
  }, []);

  // Bei jeder Fenster-Größenänderung innerhalb des sichtbaren Bereichs bleiben (kein Verlust hinter
  // dem Rand, wenn der Browser verkleinert wird oder ein angedocktes Fenster die volle Höhe hat).
  useEffect(() => {
    if (!active) return;
    const onResize = () => {
      setGeo((g) => {
        if (g.mode !== "float") return g; // angedockte Fenster folgen der Höhe automatisch (siehe style unten)
        const w = clamp(g.w, MIN_W, MAX_W);
        const h = clamp(g.h, MIN_H, window.innerHeight - EDGE_MARGIN * 2);
        const x = clamp(g.x, EDGE_MARGIN, Math.max(EDGE_MARGIN, window.innerWidth - w - EDGE_MARGIN));
        const y = clamp(g.y, EDGE_MARGIN, Math.max(EDGE_MARGIN, window.innerHeight - h - EDGE_MARGIN));
        return { ...g, w, h, x, y };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [active]);

  const commit = useCallback((g: Geometry) => {
    setGeo(g);
    writeStored(g);
  }, []);

  const onHeaderPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!active || geo.mode === "pinned") return;
      const target = e.target as HTMLElement;
      if (target.closest("button, a, input, [role='menu']")) return; // Klicks auf Bedienelemente lösen kein Ziehen aus
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const startGeo = geo;
      // Wird aus dem Andocken heraus gezogen, erst wieder frei schweben lassen (an derselben Stelle).
      const rect = e.currentTarget.closest<HTMLElement>("[data-floating-window]")?.getBoundingClientRect();
      const base: Geometry =
        startGeo.mode === "float"
          ? startGeo
          : { mode: "float", x: rect?.left ?? startGeo.x, y: rect?.top ?? EDGE_MARGIN, w: rect?.width ?? startGeo.w, h: rect?.height ?? startGeo.h };
      let next = base;
      setDragging(true);
      setGeo(base);
      const onMove = (ev: PointerEvent) => {
        const nx = base.x + (ev.clientX - startX);
        const ny = base.y + (ev.clientY - startY);
        next = { ...base, mode: "float", x: nx, y: ny };
        setGeo(next);
      };
      const onUp = (ev: PointerEvent) => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        setDragging(false);
        const nearLeft = ev.clientX < SNAP_ZONE;
        const nearRight = ev.clientX > window.innerWidth - SNAP_ZONE;
        if (nearLeft || nearRight) {
          commit({ ...next, mode: nearLeft ? "dock-left" : "dock-right" });
        } else {
          const w = clamp(next.w, MIN_W, MAX_W);
          const h = clamp(next.h, MIN_H, window.innerHeight - EDGE_MARGIN * 2);
          commit({
            mode: "float",
            w,
            h,
            x: clamp(next.x, EDGE_MARGIN, Math.max(EDGE_MARGIN, window.innerWidth - w - EDGE_MARGIN)),
            y: clamp(next.y, EDGE_MARGIN, Math.max(EDGE_MARGIN, window.innerHeight - h - EDGE_MARGIN)),
          });
        }
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [active, geo, commit],
  );

  const onResizeHandlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>, corner: ResizeCorner) => {
      if (!active || geo.mode !== "float") return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const start = geo;
      // Diagonal-Logik wie im Prototyp (schwebender-assistent.html Zeilen 685-694): die Ecke
      // bestimmt, ob beim Ziehen die linke/obere Kante fest bleibt (x/y wandert mit) oder die
      // rechte/untere (nur w/h ändert sich).
      const left = corner === "nw" || corner === "sw";
      const top = corner === "nw" || corner === "ne";
      setResizing(true);
      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        // Wachstum darf die JEWEILS FESTE Kante nicht über den sichtbaren Rand hinaus schieben:
        // bei nw/sw wandert die linke Kante mit (x sinkt) — sie darf nicht unter EDGE_MARGIN
        // fallen, also newW höchstens start.x+start.w-EDGE_MARGIN; bei ne/se bleibt x fest, dafür
        // darf die RECHTE Kante nicht über den Viewport hinaus wachsen. Symmetrisch für Höhe/y.
        // (Nur bei se — der einzigen Ecke aus dem Vorgänger-Code — war das für die Höhe schon so;
        // hier für alle vier Ecken UND für die Breite ergänzt, sonst wandert das Fenster bei
        // nw/ne/sw messbar aus dem sichtbaren Bereich, siehe Golden-Path-Test.)
        const maxWFromEdge = left ? start.x + start.w - EDGE_MARGIN : window.innerWidth - EDGE_MARGIN - start.x;
        const maxHFromEdge = top ? start.y + start.h - EDGE_MARGIN : window.innerHeight - EDGE_MARGIN - start.y;
        const newW = clamp(left ? start.w - dx : start.w + dx, MIN_W, Math.min(MAX_W, maxWFromEdge));
        const newH = clamp(top ? start.h - dy : start.h + dy, MIN_H, Math.min(window.innerHeight - EDGE_MARGIN * 2, maxHFromEdge));
        setGeo((g) => ({
          ...g,
          w: newW,
          h: newH,
          x: left ? start.x + start.w - newW : start.x,
          y: top ? start.y + start.h - newH : start.y,
        }));
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        setResizing(false);
        setGeo((g) => {
          commit(g);
          return g;
        });
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [active, geo, commit],
  );

  const collapse = useCallback(() => {
    // Merkt sich, ob GERADE verankert war — bestimmt beim Ausklappen, wohin es zurückkehrt.
    const next: CollapseState = { collapsed: true, wasPinned: geo.mode === "pinned" };
    setCollapseState(next);
    writeCollapseState(next);
  }, [geo]);

  const expand = useCallback(() => {
    const next: CollapseState = { collapsed: false, wasPinned: false };
    setCollapseState(next);
    writeCollapseState(next);
    // Kehrt NICHT zum vorherigen Modus zurück, sondern zu "pinned" (falls zuvor verankert) oder
    // sonst "dock-right" (passt visuell zur Randleiste) — exakt wie im Prototyp, Zeile 725/731.
    commit({ ...geo, mode: collapseState.wasPinned ? "pinned" : "dock-right" });
  }, [geo, collapseState.wasPinned, commit]);

  const togglePin = useCallback(() => {
    commit(geo.mode === "pinned" ? clampFloatGeometry({ ...geo, mode: "float" }) : { ...geo, mode: "pinned" });
  }, [geo, commit]);

  let style: React.CSSProperties | undefined;
  if (active && geo.mode !== "pinned") {
    if (geo.mode === "float") {
      style = { left: geo.x, top: geo.y, width: geo.w, height: geo.h };
    } else {
      const w = clamp(geo.w, MIN_W, MAX_W);
      style = {
        top: EDGE_MARGIN,
        height: `calc(100vh - ${EDGE_MARGIN * 2}px)`,
        width: w,
        ...(geo.mode === "dock-left" ? { left: 0 } : { right: 0 }),
      };
    }
  }
  // "pinned": Geometrie kommt bewusst NICHT aus `style`, sondern vollständig aus CSS-Klassen
  // (siehe CopilotPanel.tsx) — sonst überstimmen alte Inline-Werte (aus einem vorherigen
  // "float"/"dock"-Zustand) die Klasse (Prototyp-Kommentar, schwebender-assistent.html Z.587-589).

  return {
    active,
    mode: geo.mode,
    collapsed,
    dragging,
    resizing,
    style,
    onHeaderPointerDown,
    onResizeHandlePointerDown,
    collapse,
    expand,
    togglePin,
  };
}
